import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Panel } from "../components/Panel";
import { usePublicBaseUrl } from "../lib/publicBaseUrls";
import { Select } from "../components/Field";
import { Button } from "../components/Button";
import {
  topicPagesApi,
  type TopicPageStatus,
  type TopicPageRow,
} from "../api/topic-pages";

const PUBS = [
  { slug: "flight_journal", name: "Flight Journal" },
  { slug: "model_airplane_news", name: "Model Airplane News" },
  { slug: "rc_car_action", name: "RC Car Action" },
  { slug: "diecast", name: "Die Cast X" },
];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "retired", label: "Retired" },
];

function StatusBadge({ status }: { status: TopicPageStatus }) {
  const style: Record<TopicPageStatus, string> = {
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    draft: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    retired: "bg-ink-500/20 text-paper-trace border-ink-500",
  };
  return (
    <span className={`text-xs px-2 py-0.5 border rounded ${style[status]}`}>
      {status}
    </span>
  );
}

function TopicRow({ row, onPublicView }: {
  row: TopicPageRow;
  onPublicView: (slug: string) => void;
}) {
  const lockedCount = row.editor_locked_fields.length;
  const lastRun = row.agent_last_run_at
    ? new Date(row.agent_last_run_at).toLocaleString()
    : "never";
  return (
    <tr className="border-b border-ink-700">
      <td className="py-2 pr-4">
        <div className="font-serif text-paper">{row.title}</div>
        <div className="label">/topic/{row.slug}</div>
      </td>
      <td className="py-2 pr-4">
        <StatusBadge status={row.status} />
      </td>
      <td className="py-2 pr-4 label">
        {row.hero_kind === "trailer" ? "🎬 trailer" : "🖼  static"}
      </td>
      <td className="py-2 pr-4 label">
        {row.sample_article_ids?.length ?? 0} samples
      </td>
      <td className="py-2 pr-4 label" title={row.editor_locked_fields.join(", ")}>
        {lockedCount > 0
          ? <span className="text-amber-glow">{lockedCount} locked</span>
          : <span className="text-paper-trace">—</span>}
      </td>
      <td className="py-2 pr-4 label">{lastRun}</td>
      <td className="py-2">
        <Button onClick={() => onPublicView(row.slug)}>View</Button>
      </td>
    </tr>
  );
}

export function TopicPagesPage() {
  const [pub, setPub] = useState<string>(() => {
    return localStorage.getItem("topic-pages.last-pub") || "flight_journal";
  });
  const [status, setStatus] = useState<string>("all");
  const publicBase = usePublicBaseUrl(pub);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["topic-pages", pub, status],
    queryFn: () =>
      topicPagesApi.list(
        pub,
        status === "all" ? undefined : (status as TopicPageStatus),
      ),
  });

  const onPubChange = (slug: string) => {
    setPub(slug);
    localStorage.setItem("topic-pages.last-pub", slug);
  };

  const onPublicView = (topicSlug: string) => {
    if (publicBase) window.open(`${publicBase}/topic/${topicSlug}`, "_blank");
  };

  const rows = data?.topic_pages ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="font-serif text-3xl text-paper mb-2">Topic pages</h1>
      <p className="label mb-5">
        AI-curated topic landing pages — the agent populates and re-ranks;
        editors approve, override, or retire. Live URL pattern:{" "}
        <code className="text-amber-glow">{publicBase ?? "…"}/topic/&lt;slug&gt;</code>
      </p>

      <Panel>
        <div className="flex gap-3 items-end mb-4">
          <Select
            label="Publication"
            value={pub}
            onChange={(e) => onPubChange(e.target.value)}
            options={PUBS.map((p) => ({ value: p.slug, label: p.name }))}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
          <Button onClick={() => refetch()}>Refresh</Button>
        </div>

        {error && (
          <p className="text-red-400">Failed to load: {String(error)}</p>
        )}
        {isLoading && <p className="label">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="label py-6">
            No topic pages yet. The agent's discovery loop emits proposals
            into the queue — approve them on the{" "}
            <a href="/agent-proposals" className="text-amber-glow underline">
              Agent proposals
            </a>{" "}
            page to create live topic pages.
          </p>
        )}
        {!isLoading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-ink-500">
              <tr>
                <th className="text-left py-2 pr-4 label">Topic</th>
                <th className="text-left py-2 pr-4 label">Status</th>
                <th className="text-left py-2 pr-4 label">Hero</th>
                <th className="text-left py-2 pr-4 label">Samples</th>
                <th className="text-left py-2 pr-4 label">Locked</th>
                <th className="text-left py-2 pr-4 label">Last curated</th>
                <th className="py-2 label" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <TopicRow
                  key={r.id} row={r}
                  onPublicView={onPublicView}
                />
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
